import {
    createContext,
    type Accessor,
    type Setter,
    useContext,
    createSignal,
    type JSX,
    type ParentComponent,
} from "solid-js";
import type { Stage, StageError } from "../domain";

/**
 * Stage context.
 */
export type StageContextType = {
    /** Getter for the context data. */
    stageContext: Accessor<Stage | StageError | undefined>;
    /** Setter for the context data. */
    setStageContent: Setter<Stage | StageError | undefined>;
};
export const StageContextContainer = createContext<StageContextType>();

/**
 * Stage context object.
 *
 * @throws Error when used outside the stageContextProvider.
 * @returns stageContextType object.
 */
export function useStageContext(): StageContextType {
    const ctx = useContext(StageContextContainer);
    if (!ctx) throw new Error("Must be used within stageContextProvider");
    return ctx;
}

/**
 * Wrapper for handling stage context data.
 *
 * @param props - Properties to reference the child elements.
 * @returns Wrapper object with children.
 */
export const StageContextProvider: ParentComponent = (props): JSX.Element => {
    const [stageContext, setStageContent] = createSignal<Stage | StageError | undefined>();

    return (
        <StageContextContainer.Provider value={{ stageContext, setStageContent }}>
            {props.children}
        </StageContextContainer.Provider>
    );
};
